import React, { useMemo, useState } from "react";
import { IoEllipsisHorizontal } from "react-icons/io5";
import {
  BsExclamationDiamondFill,
  BsPersonFillCheck,
  BsPersonPlus,
} from "react-icons/bs";
import { listFormat, randomExtension,currentLang,capitalizeWords } from "utils/utils";
import _ from "lodash";
import { ProfileDefaultPlaceholder } from "constants/images";
import Button from "components/button/button";
import LoadingIndicator from "assets/svg/loading-indicator";
import { useTranslation } from "react-i18next";

export default function FollowingItem({
  data = {},
  onProfileClick = () => {},
  onFollow = async () => {},
  type = "following",
  isYourProfile
}) {
  const {t} = useTranslation();
  const {translation = null, user = null} = data[type] || {}
  const lang = currentLang();
  const fullName = data[type]
    ? `${translation?.[lang]?.[user?.first_name] || data[type]?.user?.first_name}
     ${translation?.[lang]?.[user?.last_name] || data[type]?.user?.last_name}`
    : null;
  const profileImage = data[type]?.profile_image_url
    ? data[type]?.profile_image_url
    : ProfileDefaultPlaceholder;
  const thumbnail = data[type]?.profile_image_webp_url || null;
  
   const occupations = useMemo(() => {
      if (!_.isEmpty(data[type]?.occupations)) {
        var capitalizedArray = data[type]?.occupations.map(capitalizeWords);
        const list = capitalizedArray.map((o) => {
          if (typeof o === "object") {
            return translation?.[lang]?.[o.value] || o.value;
          } else if (typeof o === "string") {
            return translation?.[lang]?.[o] || o;
          } else {
            return null;
          }
        });
  
        if (list) {
          const currLang = lang === 'ch' ? 'zh-cn' : lang;
          if(currLang !== 'undefined') { 
            return listFormat(list,currLang);
          }else{
            return listFormat(list,'en');
          }
        }
      }
      return "";
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data[type]?.occupations,lang]);



  // Gate adult-content profiles (Marker.io MCF-1696): when the listed profile
  // actually contains adult content (has_adult_content, from the API) AND the
  // viewer has "show adult content to me" turned off, we hide the avatar behind a
  // placeholder, make the name unclickable and show a hover popup explaining why.
  let viewerShowsAdult = false;
  try {
    viewerShowsAdult = !!JSON.parse(localStorage.getItem("user_data"))?.profile?.show_adult;
  } catch (e) {
    viewerShowsAdult = false;
  }
  const profileHasAdult = !!data[type]?.has_adult_content;
  const restrictAdult = profileHasAdult && !viewerShowsAdult;
  const extension = randomExtension();
  const [submitLoading, setSubmitLoading] = useState(false);

  const onClickFollowing = async (id) => {
    try {
      setSubmitLoading(true);
      await onFollow(id);
      setSubmitLoading(false);
    } catch (err) {
      setSubmitLoading(false);
      console.error("Error Following", err);
    }
  };

  const follow = useMemo(() => {
    if (type === "following") {
      return true;
    } else {
      return data?.did_follow_back;
    }
  }, [data, type]);

  console.log("data => ", data);

  return (
    <div className="w-full bg-white rounded-md p-5 4sm:p-10 mb-5">
      <div className="w-full 1md:flex 1md:items-center 1md:justify-between">
        {/* left */}
        <div className="flex space-x-5 items-start">
          {/* image */}
          <div className="w-15 h-15 rounded-md overflow-hidden">
            <img
              src={restrictAdult ? ProfileDefaultPlaceholder : (thumbnail || profileImage)}
              alt="milestone-avatar"
              className="w-full h-full"
            />
          </div>
          {/* details */}
          <div>
            <div className="flex flex-col md:flex-row md:space-x-2">
              {restrictAdult ? (
                // Adult-content profile + viewer has adult off: name is not
                // clickable and a popup explains why on hover. (MCF-1696)
                <div className="relative group inline-flex items-center">
                  <p className="text-base 4sm:text-lg font-bold leading-5.5 tracking-tighter text-darkerGray cursor-default select-none flex items-center">
                    <BsExclamationDiamondFill className="text-red text-base mr-2" />
                    {fullName}
                  </p>
                  <div className="pointer-events-none absolute left-0 top-full z-30 mt-1 hidden group-hover:block w-64 rounded-md bg-lightBlack text-white text-xs leading-5 p-3 shadow-lg">
                    {t(
                      "adultContentHiddenNotice",
                      "This profile contains adult content. You have set your profile to not show adult content."
                    )}
                  </div>
                </div>
              ) : (
                <button
                  className="flex items-center cursor-pointer"
                  onClick={() =>
                    onProfileClick(
                      `${data[type]?.username}-${extension}`, `${data[type]?.id}`
                    )
                  }>
                  <p className="text-base 4sm:text-lg font-bold leading-5.5 tracking-tighter">
                    {fullName}
                  </p>
                </button>
              )}
            </div>
            <p className="text-xs text-darkerGray leading-6">
              {translation?.[lang]?.[data[type]?.address] || data[type]?.address || ''}
            </p>
            <p className="text-base leading-5 hidden 4sm:block mt-2">
              {_.toString(occupations)}
            </p>
          </div>
        </div>
        {/* right */}
        {
          isYourProfile && 
            <div className="flex flex-row space-x-3.6 mt-3.6 1md:mt-0">
              <Button
                buttonName={`${follow ? t("following") : t("follow")}`}
                buttonTextClass={`text-xs 3md:text-sm ${
                  follow ? "text-white" : "text-lightBlack"
                } font-semibold flex items-center`}
                iconPosition="left"
                icon={
                  follow ? (
                    <BsPersonFillCheck className="mr-3 text-lg" />
                  ) : (
                    <BsPersonPlus className="mr-3 text-lg" />
                  )
                }
                buttonRadius="6px"
                buttonClass={`relative py-2 3md:py-3.6 px-5 ${
                  follow
                    ? "bg-primary hover:bg-darkPrimary"
                    : "bg-thBg hover:bg-gray"
                } `}
                loadingIcon={
                  submitLoading ? (
                    <LoadingIndicator className="text-blue-500 w-4 h-4 ml-2" />
                  ) : null
                }
                onClick={() => onClickFollowing(data[type]?.id)}
              />
              {/* <button className="py-2 3md:py-3.6 px-4 bg-thBg rounded-md">
                <IoEllipsisHorizontal className="text-base 4sm:text-2xl text-lightBlack" />
              </button> */}
            </div>
        }
      </div>
    </div>
  );
}
